const cities = [
  { name: "Winter Park", zips: "32789, 32792" },
  { name: "Orlando", zips: "32801" },
  { name: "Maitland", zips: "32751" },
  { name: "Altamonte Springs", zips: "32701" },
  { name: "Casselberry", zips: "32707" },
  { name: "Oviedo", zips: "32765" },
  { name: "Longwood", zips: "32750" },
  { name: "Lake Mary", zips: "32746" },
  { name: "Sanford", zips: "32771" },
  { name: "Kissimmee", zips: "34741" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home, campus, or Park Avenue in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function WinterParkLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Winter Park & Greater Orlando
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Winter Park, Orlando, Maitland, Altamonte Springs, or anywhere in Orange and Seminole Counties, Fast Passport Center makes expedited passport processing simple — no drive to a passport agency, no lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Winter Park is one of Central Florida's most distinctive communities — a city that feels more like a New England college town dropped into the Sunshine State than a typical Orlando suburb. Founded in the 1880s as a winter resort for wealthy Northern industrialists, Winter Park developed around a chain of pristine lakes, a walkable brick-paved downtown, and a cultural sophistication rare in Florida. Today it is home to roughly 30,000 residents, Rollins College (the oldest college in Florida, founded in 1885), the Charles Hosmer Morse Museum of American Art (housing the world's most comprehensive collection of Tiffany glass), and Park Avenue — one of the most charming shopping and dining districts in the American South.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Winter Park unique from a passport and travel perspective is the extraordinary concentration of internationally oriented activity in a relatively small geographic footprint. Rollins College sends hundreds of students abroad every year through programs in Spain, Italy, Japan, the UK, and Costa Rica. The college's international business and international relations programs attract students who plan careers requiring global mobility. Faculty travel for research, conferences, and sabbaticals on timelines that academic calendars compress into narrow windows. A student accepted to a summer program in Barcelona in March may need a passport renewed or issued by May — a timeline standard government processing simply cannot meet.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Beyond academia, Winter Park's residents are among the most affluent and well-traveled in Central Florida. The city's historic homes along Interlaken Avenue, Genius Drive, and the shores of Lake Osceola and Lake Maitland belong to professionals, entrepreneurs, physicians, and attorneys who travel internationally for business and leisure. The Park Avenue corridor draws visitors from across the region for its boutiques, galleries, and restaurants, but its residents are the ones flying to London for theatre weekends, to the Caribbean for family cruises, and to Europe for extended summer holidays.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Winter Park's proximity to Orlando International Airport (MCO) — just 15 to 20 minutes south on State Road 423 or Interstate 4 — makes it ideally positioned for international travel. MCO is one of the busiest airports in the United States and offers direct flights to London, Paris, Frankfurt, São Paulo, Mexico City, and dozens of Caribbean destinations. For Winter Park residents, the airport is close enough to reach with minimal stress but far enough that discovering a passport issue the morning of a flight becomes a genuine crisis.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city's family demographics also create unique passport demand patterns. Winter Park is home to excellent public schools (Winter Park High School is consistently ranked among Florida's best) and prestigious private academies. Families here plan spring break trips to Europe, summer programs in Costa Rica, and winter Caribbean cruises with the same intentionality they apply to school applications. But children's passports expire in five years, and a family of four may discover that three of their four passports are invalid within months of each other.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Winter Park travelers across every demographic — Rollins students preparing for semester abroad, Park Avenue professionals with last-minute London conferences, and Baldwin Park families scrambling to replace a child's expired passport before a Disney cruise. Our courier-based expedited service means you start online from your Winter Park home, your Rollins dorm, or your Park Avenue office — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Orlando</p>
                  <p className="text-white/60 text-xs mt-0.5">200 S Orange Ave, Orlando, FL 32801</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Serving Winter Park — approximately 15 miles south via I-4 to downtown Orlando
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout the Orlando metro area</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or Rollins campus in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location on Park Avenue or Aloma Avenue, or <strong className="text-white">schedule a drop-off</strong> at our Orlando office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities & Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Orange and Seminole Counties — start online, ship overnight, and we handle the rest. No need to drive to a regional passport agency.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Orlando", href: "/orlando" },
                  { name: "Windermere", href: "/windermere" },
                  { name: "Winter Garden", href: "/winter-garden" },
                  { name: "Celebration", href: "/celebration" },
                  { name: "Lake Mary", href: "/lake-mary" },
                  { name: "Tampa", href: "/tampa" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}